import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {newRepoPath} from '@github-ui/paths'
import {useLocation, useNavigation, useNavigationType} from '@github-ui/react-router'
import {ShortcutOptionsButton, type SidebarLink} from '@github-ui/saved-views'
import {ActionList, Banner, Button, Truncate} from '@primer/react'

const focusOnMount = (element: HTMLAnchorElement | null) => {
  if (!element) return

  const animationFrame = requestAnimationFrame(() => element.focus())
  return () => cancelAnimationFrame(animationFrame)
}

export interface FinderHeaderProps {
  selectedSidebarItem: SidebarLink | undefined
  onEditView: (shortcut: SidebarLink) => void
  onDeleteView: (shortcut: SidebarLink) => void
  onUpdateQueryView: (shortcut: SidebarLink) => void
  isQueryModified?: boolean
  deleteErrorMessage?: string
}

export function FinderHeader({
  selectedSidebarItem,
  onEditView,
  onDeleteView,
  onUpdateQueryView,
  isQueryModified = false,
  deleteErrorMessage,
}: FinderHeaderProps) {
  const {name = 'Search', description} = selectedSidebarItem || {}
  const location = useLocation()
  const navigation = useNavigation()
  const navigationType = useNavigationType()
  const shouldFocusHeaderAction =
    navigation.state === 'idle' && navigationType !== 'POP' && location.state?.focusHeaderAction === true

  return (
    <>
      {deleteErrorMessage && (
        <Banner variant="critical" title="Error" hideTitle description={deleteErrorMessage} className="tmp-mb-3" />
      )}
      <FilteredListPageLayout.PageHeader>
        <FilteredListPageLayout.PageHeader.TitleArea className="min-width-0">
          <FilteredListPageLayout.PageHeader.Title as="h1" className="min-width-0">
            <Truncate title={name} maxWidth="100%">
              {name}
            </Truncate>
          </FilteredListPageLayout.PageHeader.Title>
        </FilteredListPageLayout.PageHeader.TitleArea>
        <FilteredListPageLayout.PageHeader.Actions>
          <FilteredListPageLayout.HeaderActions
            menuAriaLabel="More repository actions"
            actions={[
              ...(selectedSidebarItem?.source === 'user'
                ? [
                    {
                      key: 'view-options',
                      alwaysInline: true,
                      renderInline: () => (
                        <ShortcutOptionsButton
                          onEditView={() => onEditView(selectedSidebarItem)}
                          onDeleteView={() => onDeleteView(selectedSidebarItem)}
                          onUpdateQueryView={() => onUpdateQueryView(selectedSidebarItem)}
                          isQueryModified={isQueryModified}
                        />
                      ),
                      // Required by the custom HeaderAction shape; this action
                      // is always inline so the menu item path is not used.
                      renderMenuItem: () => <ActionList.Item disabled>View options</ActionList.Item>,
                    },
                  ]
                : []),
              {
                key: 'new-repo',
                alwaysInline: true,
                renderInline: () => (
                  <Button
                    key={shouldFocusHeaderAction ? location.key : 'default'}
                    ref={shouldFocusHeaderAction ? focusOnMount : undefined}
                    as="a"
                    href={newRepoPath()}
                    variant="primary"
                  >
                    New repository
                  </Button>
                ),
                renderMenuItem: () => <ActionList.LinkItem href={newRepoPath()}>New repository</ActionList.LinkItem>,
              },
            ]}
          />
        </FilteredListPageLayout.PageHeader.Actions>
        {description && (
          <FilteredListPageLayout.PageHeader.Description>
            <Truncate title={description} maxWidth="100%">
              {description}
            </Truncate>
          </FilteredListPageLayout.PageHeader.Description>
        )}
      </FilteredListPageLayout.PageHeader>
    </>
  )
}
